import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { ActionIcon, Box, Stack, Text } from "@mantine/core";
import { IconPencil } from "@tabler/icons-react";
import { createColumnHelper } from "@tanstack/react-table";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { EditUsersAssignedToRole } from "../-EditUsersAssignedToRole";
import type { CommunityDetailsRoleAssignment } from "@/models/CommunityDetails";
import { SparkRoleFullDisplayName, SparkRoleKey } from "@/models/SparkRole";
import { getCommunityDetailsByIdQuery, useApi } from "@/api";
import LoadingPane from "@/components/LoadingPane";
import { DataTable } from "@/components/DataTable";
import {
  canSetCommunityAdmin,
  canSetOtherRoleAssignments
} from "@/security/logic";

export const Route = createFileRoute("/settings/community/$communityId/roles")({
  component: RouteComponent,
  loader: () => {
    return {
      crumb: "Roles"
    };
  }
});

interface RoleRow {
  roleFullDisplayName: string;
  names: string[];
  roleKey: string;
}

interface AssignedUser {
  userId: number;
  entraObjectId: string;
  displayName: string | null;
  userPrincipalName: string | null;
  checked: boolean;
}

function RouteComponent() {
  const { communityId } = Route.useParams();
  const { userClaim } = Route.useRouteContext();
  const { secureAxios } = useApi();
  const queryClient = useQueryClient();
  const { data: community } = useQuery(
    getCommunityDetailsByIdQuery(secureAxios, Number(communityId))
  );
  const [modalOpened, setModalOpened] = useState(false);
  const [selectedRoleFullDisplayName, setSelectedRoleFullDisplayName] =
    useState<string | null>(null);
  const [selectedRoleKey, setSelectedRoleKey] = useState<string | null>(null);
  const [initialAssignedUsers, setInitialAssignedUsers] = useState<
    AssignedUser[]
  >([]);
  const [currentAssignedUsers, setCurrentAssignedUsers] = useState<
    AssignedUser[]
  >([]);

  const columnHelper = createColumnHelper<RoleRow>();

  const roleRows = useMemo(() => {
    if (!community) {
      return [];
    }

    // Group role assignments by role name from server
    const roleMap = new Map<string, CommunityDetailsRoleAssignment[]>();

    community.roleAssignments.forEach((assignment) => {
      const roleKey = assignment.sparkRoleKey || "Unknown";
      if (!roleMap.has(roleKey)) {
        roleMap.set(roleKey, []);
      }
      roleMap.get(roleKey)!.push(assignment);
    });

    // Create rows for all SparkRoles
    const rows: RoleRow[] = Object.values(SparkRoleKey).map((roleName) => {
      const assignments = roleMap.get(roleName) || [];
      const names = assignments.map(
        (a) => a.displayName || a.userPrincipalName || "Unknown"
      );

      return {
        roleFullDisplayName: SparkRoleFullDisplayName[roleName],
        roleKey: roleName,
        names: names
      };
    });

    return rows;
  }, [community]);

  if (!community) {
    return <LoadingPane />;
  }

  // Check if user can edit any role assignments
  const canEditCommunityAdmin = canSetCommunityAdmin(userClaim, community.id);
  const canEditOtherRoles = canSetOtherRoleAssignments(userClaim, community.id);
  const canEditAnyRole = canEditCommunityAdmin || canEditOtherRoles;

  // Build columns - only include Edit column if user can edit something
  const columns = [
    columnHelper.accessor("roleFullDisplayName", {
      header: "Role",
      meta: { style: { verticalAlign: "top" } }
    }),
    columnHelper.accessor("names", {
      header: "Name(s)",
      cell: ({ getValue }) =>
        getValue().map((name) => <div key={name}>{name}</div>),
      meta: { style: { verticalAlign: "top" } }
    }),
    ...(canEditAnyRole
      ? [
          columnHelper.display({
            id: "actions",
            header: "Edit",
            meta: {
              style: { verticalAlign: "top", textAlign: "center", width: "1%" }
            },
            cell: ({ row }) => {
              const roleKey = row.original.roleKey;
              // Only show edit icon for CommunityAdmin if user can set community admin
              if (
                roleKey === SparkRoleKey.CommunityAdmin &&
                !canEditCommunityAdmin
              ) {
                return null;
              }
              // Only show edit icon for other roles if user can set other role assignments
              if (
                roleKey !== SparkRoleKey.CommunityAdmin &&
                !canEditOtherRoles
              ) {
                return null;
              }

              return (
                <ActionIcon
                  variant="subtle"
                  onClick={() => {
                    setSelectedRoleFullDisplayName(
                      row.original.roleFullDisplayName
                    );
                    setSelectedRoleKey(roleKey);

                    // Get current assignments for this role
                    const currentAssignments = community.roleAssignments
                      .filter((a) => a.sparkRoleKey === roleKey)
                      .map((a) => ({
                        userId: a.userId,
                        entraObjectId: a.entraObjectId || "",
                        displayName: a.displayName,
                        userPrincipalName: a.userPrincipalName,
                        checked: true
                      }));
                    setInitialAssignedUsers(currentAssignments);
                    setCurrentAssignedUsers([]);
                    setModalOpened(true);
                  }}
                >
                  <IconPencil size={16} />
                </ActionIcon>
              );
            }
          })
        ]
      : [])
  ];

  return (
    <Stack gap={0}>
      <Text size="sm" c="dimmed" mb="md">
        A list of the community-assignable roles in the app, and all the users
        assigned to them at this community. (This is the same information as the
        Users tab, displayed in a different way.) <br />
        If blank, no one is in that role at this community.
      </Text>
      <Box w={600}>
        <DataTable data={roleRows} columns={columns} />

        <EditUsersAssignedToRole
          opened={modalOpened}
          onClose={() => {
            setModalOpened(false);
            setCurrentAssignedUsers([]);
          }}
          onSuccess={() => {
            void queryClient.refetchQueries({
              queryKey: getCommunityDetailsByIdQuery(secureAxios, community.id)
                .queryKey
            });
            setCurrentAssignedUsers([]);
          }}
          roleFullDisplayName={selectedRoleFullDisplayName}
          roleKey={selectedRoleKey}
          communityId={community.id}
          communityName={community.name}
          initialAssignedUsers={initialAssignedUsers}
          currentAssignedUsers={
            currentAssignedUsers.length > 0 ? currentAssignedUsers : undefined
          }
        />
      </Box>
    </Stack>
  );
}
